import { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import { subscriptionApi } from '@/api/subscriptions';
import { PLANS } from '@/data/plans';

type PaymentMethod = 'credit_card' | 'pix';

const formatCardNumber = (v: string) =>
  v.replace(/\D/g, '').slice(0, 16).replace(/(.{4})/g, '$1 ').trim();

const formatExpiry = (v: string) => {
  const digits = v.replace(/\D/g, '').slice(0, 6);
  return digits.length > 2 ? `${digits.slice(0, 2)}/${digits.slice(2)}` : digits;
};

const formatDocument = (v: string) => {
  const digits = v.replace(/\D/g, '').slice(0, 14);
  if (digits.length <= 11) {
    return digits
      .replace(/(\d{3})(\d)/, '$1.$2')
      .replace(/(\d{3})(\d)/, '$1.$2')
      .replace(/(\d{3})(\d{1,2})$/, '$1-$2');
  }
  return digits
    .replace(/(\d{2})(\d)/, '$1.$2')
    .replace(/(\d{3})(\d)/, '$1.$2')
    .replace(/(\d{3})(\d)/, '$1/$2')
    .replace(/(\d{4})(\d{1,2})$/, '$1-$2');
};

export function CheckoutPaymentPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const planCode = (searchParams.get('plan') || 'group') as 'group' | 'unlimited';
  const couponCode = searchParams.get('coupon') || '';

  const planData = PLANS[planCode as keyof typeof PLANS];

  const [method, setMethod] = useState<PaymentMethod>('credit_card');
  const [document, setDocument] = useState('');
  const [phone, setPhone] = useState('');
  const [cardNumber, setCardNumber] = useState('');
  const [cardHolder, setCardHolder] = useState('');
  const [expiry, setExpiry] = useState('');
  const [cvv, setCvv] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [pixQr, setPixQr] = useState<{ qr_code?: string; qr_code_url?: string } | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSubmitting(true);

    const rawDoc = document.replace(/\D/g, '');
    const [expMonth, expYear] = expiry.split('/');

    try {
      const result = await subscriptionApi.initiatePayment({
        plan_code: planCode,
        payment_method_type: method,
        customer_document: rawDoc,
        customer_phone: phone.replace(/\D/g, '') || undefined,
        coupon_code: couponCode || undefined,
        ...(method === 'credit_card' && {
          card_number: cardNumber.replace(/\s/g, ''),
          card_holder_name: cardHolder,
          card_exp_month: expMonth,
          card_exp_year: expYear,
          card_cvv: cvv,
        }),
      });

      if (result.status === 'active') {
        navigate('/checkout/success');
      } else if (result.pix_qr_code) {
        setPixQr({ qr_code: result.pix_qr_code, qr_code_url: result.pix_qr_code_url ?? undefined });
      } else {
        navigate('/checkout/success');
      }
    } catch (err: any) {
      setError(err?.response?.data?.detail || 'Erro ao processar pagamento. Verifique os dados e tente novamente.');
    } finally {
      setSubmitting(false);
    }
  };

  if (pixQr) {
    return (
      <div className="min-h-screen bg-[#0A0A0F]">
        <Navbar />
        <div className="pt-32 pb-20 px-6 flex items-center justify-center">
          <div className="w-full max-w-md text-center">
            <div className="w-16 h-16 rounded-full bg-green-500/20 flex items-center justify-center mx-auto mb-6">
              <i className="ri-qr-code-line text-3xl text-green-400"></i>
            </div>
            <h2 className="text-2xl font-semibold text-white mb-2" style={{ fontFamily: "'Playfair Display', serif" }}>
              Pague com PIX
            </h2>
            <p className="text-white/50 text-sm mb-8">
              Abra o app do seu banco, escaneie o QR code ou copie o código abaixo.
              Seu acesso é liberado automaticamente após o pagamento.
            </p>

            {pixQr.qr_code_url && (
              <img src={pixQr.qr_code_url} alt="QR Code PIX" className="mx-auto mb-6 rounded-xl w-56 h-56 bg-white p-3" />
            )}

            {pixQr.qr_code && (
              <div className="mb-6">
                <p className="text-white/40 text-xs mb-2">Código PIX Copia e Cola</p>
                <div className="bg-[#111111] border border-white/10 rounded-xl p-4 break-all text-white/70 text-xs font-mono">
                  {pixQr.qr_code}
                </div>
                <button
                  onClick={() => navigator.clipboard.writeText(pixQr.qr_code!)}
                  className="mt-2 text-xs text-[#C8A96E] hover:text-[#D4B882] cursor-pointer"
                >
                  Copiar código
                </button>
              </div>
            )}

            <p className="text-white/30 text-xs">
              Aguardando confirmação do pagamento...
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0A0A0F]">
      <Navbar />

      <div className="pt-32 pb-20 px-6">
        <div className="max-w-lg mx-auto">
          <div className="text-center mb-10">
            <span className="text-[#C8A96E] text-xs uppercase tracking-widest">Finalizar assinatura</span>
            <h1 className="text-3xl font-light text-white mt-2" style={{ fontFamily: "'Playfair Display', serif" }}>
              Plano <strong>{planData?.name}</strong>
            </h1>
          </div>

          {error && (
            <div className="bg-red-500/10 border border-red-500/20 rounded-xl p-4 mb-6">
              <p className="text-sm text-red-400">{error}</p>
            </div>
          )}

          <form onSubmit={handleSubmit} className="flex flex-col gap-6">
            {/* Método de pagamento */}
            <div className="bg-[#111111] border border-white/5 rounded-2xl p-6">
              <p className="text-white/50 text-xs uppercase tracking-widest mb-4">Forma de pagamento</p>
              <div className="grid grid-cols-2 gap-3">
                {(['credit_card', 'pix'] as PaymentMethod[]).map((m) => (
                  <button
                    key={m}
                    type="button"
                    onClick={() => setMethod(m)}
                    className={`py-3 rounded-xl text-sm font-medium transition-all border cursor-pointer ${
                      method === m
                        ? 'border-[#C8A96E] bg-[#C8A96E]/10 text-[#C8A96E]'
                        : 'border-white/10 text-white/50 hover:border-white/20'
                    }`}
                  >
                    <i className={`${m === 'credit_card' ? 'ri-bank-card-line' : 'ri-qr-code-line'} mr-2`}></i>
                    {m === 'credit_card' ? 'Cartão' : 'PIX'}
                  </button>
                ))}
              </div>
            </div>

            {/* Dados pessoais */}
            <div className="bg-[#111111] border border-white/5 rounded-2xl p-6">
              <p className="text-white/50 text-xs uppercase tracking-widest mb-4">Seus dados</p>
              <div className="flex flex-col gap-4">
                <div>
                  <label className="text-white/50 text-xs mb-1 block">CPF ou CNPJ *</label>
                  <input
                    value={document}
                    onChange={(e) => setDocument(formatDocument(e.target.value))}
                    placeholder="000.000.000-00"
                    required
                    className="w-full bg-[#0A0A0F] border border-white/10 rounded-xl px-4 py-3 text-white placeholder-white/20 focus:border-[#C8A96E] outline-none transition-all"
                  />
                </div>
                <div>
                  <label className="text-white/50 text-xs mb-1 block">Telefone (opcional)</label>
                  <input
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                    placeholder="(11) 99999-9999"
                    className="w-full bg-[#0A0A0F] border border-white/10 rounded-xl px-4 py-3 text-white placeholder-white/20 focus:border-[#C8A96E] outline-none transition-all"
                  />
                </div>
              </div>
            </div>

            {/* Dados do cartão */}
            {method === 'credit_card' && (
              <div className="bg-[#111111] border border-white/5 rounded-2xl p-6">
                <p className="text-white/50 text-xs uppercase tracking-widest mb-4">Dados do cartão</p>
                <div className="flex flex-col gap-4">
                  <div>
                    <label className="text-white/50 text-xs mb-1 block">Número do cartão *</label>
                    <input
                      value={cardNumber}
                      onChange={(e) => setCardNumber(formatCardNumber(e.target.value))}
                      placeholder="0000 0000 0000 0000"
                      required
                      inputMode="numeric"
                      className="w-full bg-[#0A0A0F] border border-white/10 rounded-xl px-4 py-3 text-white placeholder-white/20 focus:border-[#C8A96E] outline-none transition-all font-mono tracking-widest"
                    />
                  </div>
                  <div>
                    <label className="text-white/50 text-xs mb-1 block">Nome no cartão *</label>
                    <input
                      value={cardHolder}
                      onChange={(e) => setCardHolder(e.target.value.toUpperCase())}
                      placeholder="NOME COMO NO CARTÃO"
                      required
                      className="w-full bg-[#0A0A0F] border border-white/10 rounded-xl px-4 py-3 text-white placeholder-white/20 focus:border-[#C8A96E] outline-none transition-all"
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="text-white/50 text-xs mb-1 block">Validade *</label>
                      <input
                        value={expiry}
                        onChange={(e) => setExpiry(formatExpiry(e.target.value))}
                        placeholder="MM/AAAA"
                        required
                        inputMode="numeric"
                        className="w-full bg-[#0A0A0F] border border-white/10 rounded-xl px-4 py-3 text-white placeholder-white/20 focus:border-[#C8A96E] outline-none transition-all font-mono"
                      />
                    </div>
                    <div>
                      <label className="text-white/50 text-xs mb-1 block">CVV *</label>
                      <input
                        value={cvv}
                        onChange={(e) => setCvv(e.target.value.replace(/\D/g, '').slice(0, 4))}
                        placeholder="123"
                        required
                        inputMode="numeric"
                        type="password"
                        className="w-full bg-[#0A0A0F] border border-white/10 rounded-xl px-4 py-3 text-white placeholder-white/20 focus:border-[#C8A96E] outline-none transition-all font-mono"
                      />
                    </div>
                  </div>
                </div>
              </div>
            )}

            {method === 'pix' && (
              <div className="bg-[#C8A96E]/5 border border-[#C8A96E]/20 rounded-2xl p-5 text-center">
                <i className="ri-qr-code-line text-3xl text-[#C8A96E] mb-2 block"></i>
                <p className="text-white/60 text-sm">
                  Após confirmar, você receberá um QR Code para pagar via PIX.
                  O acesso é liberado automaticamente após confirmação.
                </p>
              </div>
            )}

            <button
              type="submit"
              disabled={submitting}
              className="w-full py-4 bg-[#C8A96E] text-[#0A0A0F] rounded-xl font-semibold text-base hover:bg-[#D4B882] disabled:opacity-60 transition-all cursor-pointer"
            >
              {submitting
                ? 'Processando...'
                : method === 'pix'
                  ? 'Gerar QR Code PIX'
                  : 'Confirmar pagamento'}
            </button>

            <p className="text-center text-white/30 text-xs">
              Pagamento processado com segurança via PagarMe.
              Você pode cancelar a qualquer momento.
            </p>
          </form>
        </div>
      </div>
    </div>
  );
}
